/**
 * Bake-cut helpers for the Control Surfaces workbench (pure + the poll loop).
 *
 * The backend cut route is job-shaped: POST /control-surface/cut returns a
 * handle, GET /cut/{job_id} polls to a result whose controls[].outputs[]
 * .step_path are the baked geometry files on disk (.stl from the shaped
 * cutter's mesh booleans; box modes write .stp - /geometry/tessellate
 * displays both). pollCutJob drives that handle to a
 * terminal status - tolerating a few transient fetch failures, because the
 * dev API restarts under our feet and the job table is in-memory (a 404
 * mid-poll therefore means the job is gone for good, not "not yet").
 * flattenBakeOutputs turns the completed result into the flat list the 3D
 * pane's baked-mesh toggle/selector consumes. Kept free of component state
 * so the response parsing is unit-testable (controlSurfaceBake.test.ts).
 */

import type { BakeCutJobResult, BakeCutJobStatus } from "../../api/controlSurface";

/** One selectable baked mesh: control name + deflection + its file on the API host. */
export interface BakedOutputRef {
  /** Control name as cut (mirror copies carry the ' - mirror' suffix). */
  control: string;
  /** Baked deflection, or null when the backend reported none. */
  deflectionDeg: number | null;
  /** Absolute STEP/STL path on the API host - feed it to /geometry/tessellate. */
  stepPath: string;
}

/**
 * Flatten a completed job result into selectable baked outputs, order
 * preserved (controls as cut, deflections within each). Controls that
 * produced nothing and outputs without a path are skipped - their failures
 * ride the result's errors list, not this one.
 */
export function flattenBakeOutputs(
  result: BakeCutJobResult | null | undefined,
): BakedOutputRef[] {
  const out: BakedOutputRef[] = [];
  for (const ctrl of result?.controls ?? []) {
    for (const o of ctrl.outputs ?? []) {
      if (!o.step_path) continue;
      out.push({
        control: ctrl.name,
        deflectionDeg:
          typeof o.deflection_deg === "number" && Number.isFinite(o.deflection_deg)
            ? o.deflection_deg
            : null,
        stepPath: o.step_path,
      });
    }
  }
  return out;
}

/** 'aileron at 15 deg' (just the control name when no deflection was reported). */
export function bakedOutputLabel(ref: BakedOutputRef): string {
  return ref.deflectionDeg === null
    ? ref.control
    : `${ref.control} at ${ref.deflectionDeg} deg`;
}

export interface PollCutJobOptions<S extends Pick<BakeCutJobStatus, "status"> = BakeCutJobStatus> {
  /** Delay between polls (default 1500ms). */
  intervalMs?: number;
  /** Overall budget before giving up (default 600s - a shaped cut on real
   *  aircraft CAD runs ~80s per side PLUS the per-job section extraction,
   *  so a mirrored bake legitimately takes several minutes). */
  timeoutMs?: number;
  /** Consecutive fetch failures tolerated before rethrowing (default 4). */
  maxTransientFailures?: number;
  /** Checked before every poll - return true to stop quietly (resolves null). */
  isCancelled?: () => boolean;
  /** Called with each non-terminal status (progress copy for the UI). */
  onProgress?: (status: S) => void;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Poll a cut job to completion. Resolves with the TERMINAL status ('complete',
 * 'error' or 'cancelled' - the caller inspects it), or null when locally
 * cancelled via isCancelled. Throws on: a 404 (the in-memory job table died
 * with an API restart - unrecoverable), any other 4xx (a real request error,
 * not worth retrying), too many consecutive fetch failures, or the overall
 * timeout.
 *
 * Generic over the status record: only `status` is read here, so the nTop
 * frame-bake job (NtopBakeJobStatus - the same shape with `result` carrying a
 * frames manifest instead of cut outputs) polls through this loop unchanged.
 */
export async function pollCutJob<S extends Pick<BakeCutJobStatus, "status"> = BakeCutJobStatus>(
  fetchStatus: () => Promise<S>,
  opts: PollCutJobOptions<S> = {},
): Promise<S | null> {
  const interval = opts.intervalMs ?? 1500;
  const timeout = opts.timeoutMs ?? 600_000;
  const maxFailures = opts.maxTransientFailures ?? 4;
  const started = Date.now();
  let failures = 0;
  for (;;) {
    if (opts.isCancelled?.()) return null;
    try {
      const status = await fetchStatus();
      failures = 0;
      if (
        status.status === "complete" ||
        status.status === "error" ||
        status.status === "cancelled"
      ) {
        return status;
      }
      opts.onProgress?.(status);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      // The job table is in-memory: a 404 mid-poll means the API restarted
      // and the job died with it - retrying cannot bring it back.
      if (/\bAPI 404\b/.test(message)) {
        throw new Error("cut job lost - the API restarted mid-bake; bake again");
      }
      // Other 4xx are real request errors (auth, scope) - not transient.
      if (/\bAPI 4\d\d\b/.test(message)) throw e;
      failures += 1;
      if (failures > maxFailures) throw e;
    }
    if (Date.now() - started >= timeout) {
      throw new Error("cut timed out - the job may still be running server-side");
    }
    await sleep(interval);
  }
}
